import { call, put, takeLatest, delay } from 'redux-saga/effects';
import axios from 'axios';
import { REQUEST, fetchSuccess, fetchFailure } from '../../actions/auth/refreshToken';
import config from '../../../config';
import { getLocalUser } from '../../../utils/localUser';
import { applyCellRouting } from '../../../cellRouting';
import realtimeClient from '../../../realtime/wsClient';
import { Error as ErrorAction } from '../../actions/error/error';
import { extractErrorMessage } from '../../../utils/apiResponseHandler';

function* operation() {
  try {
    const oldToken = localStorage.getItem('authToken');
    
    if (!oldToken) {
      throw new TypeError('No token to refresh');
    }
    
    const response = yield call(axios.post, 
      `${config.API_URL}/user/refresh-token`,
      {},
      { headers: getLocalUser() }
    );
    
    // Tolerate both the raw shape and the ``data`` envelope (responseJSON),
    // same as the login sagas — the backend wraps success payloads in {data}.
    const payload = response.data?.data ?? response.data ?? {};
    const { token: newToken, cell, api_base_url: apiBaseUrl } = payload;

    if (!newToken) {
      // Never overwrite a valid token with undefined — that would poison the
      // session and force a logout on the next request.
      throw new TypeError('refresh-token response missing token');
    }

    // Update stored token
    localStorage.setItem('authToken', newToken);

    // Keep the cell routing fresh (the home cell can be re-confirmed on refresh).
    yield call(applyCellRouting, { token: newToken, cell, apiBaseUrl });

    // Re-open the realtime socket with the new token so the long-lived WS
    // connection doesn't outlive the JWT api-key claim the gateway validates
    // on connect. No-op if the socket isn't running.
    realtimeClient.reconnectWithFreshToken();

    yield put(fetchSuccess(payload));
  } catch (error) {
    yield put(ErrorAction(error));
    yield put(fetchFailure(extractErrorMessage(error)));
  }
}

// Helper function to check if token expires soon
function isTokenExpiringSoon(token, minutesBeforeExpiry = 30) {
  try {
    // Remove "Bearer " prefix if present
    const tokenStr = token.replace('Bearer ', '');
    
    // Decode JWT payload (base64 decode middle part)
    const payload = JSON.parse(atob(tokenStr.split('.')[1]));
    
    // Get expiry time in milliseconds
    const expiryTime = payload.exp * 1000;
    
    // Calculate time left
    const timeLeft = expiryTime - Date.now();
    
    // Check if less than specified minutes remaining
    return timeLeft < (minutesBeforeExpiry * 60 * 1000);
  } catch (error) {
    console.error('Error parsing token:', error);
    return true; // Assume expiring to trigger refresh
  }
}

// Auto-refresh saga that runs continuously
function* autoRefreshSaga() {
  while (true) {
    try {
      yield delay(60000); // Check every minute
      
      const token = localStorage.getItem('authToken');
      
      if (token && isTokenExpiringSoon(token, 30)) {
        yield put({ type: REQUEST });
      }
    } catch (error) {
      console.error('Auto-refresh check failed:', error);
    }
  }
}

export function* watch() {
  yield takeLatest(REQUEST, operation);
}

export function* watchAutoRefresh() {
  yield call(autoRefreshSaga);
}
